/* eslint-disable react-hooks/exhaustive-deps */
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import AddReviewModal from "./AddReviewModal";
import { useNavigate } from "react-router-dom";
import { getFormByFormID } from "./fetchData";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { convertUTCEpochToDate } from "../../../helper/DateConverters";
import { Image } from "primereact/image";

const ViewDetection = () => {
  const toast = useRef<Toast>(null);
  const ImageNotAvailable = process.env.REACT_APP_IMAGE_NOT_FOUND_URL;

  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState<any>(null);
  const [formDataLoading, setFormDataLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  const query = new URLSearchParams(window.location.search);
  const formIDParam = Number(query.get("formID"));

  const icon = (
    <i
      className="pi pi-eye tw-text-white tw-h-full tw-w-full tw-justify-center tw-items-center tw-flex"
      style={{
        backgroundColor: "black",
        opacity: 0.5,
        transition: "opacity 0.3s ease",
      }}
    ></i>
  );

  const [refreshKey, setRefreshKey] = useState(0); // Add this line

  useEffect(() => {
    if (!formIDParam) {
      navigate("/layout/leak-detection/team-listing");
    }
    getFormByFormID(
      setFormData,
      setFormDataLoading,
      formIDParam,
      navigate,
      toast,
    );
  }, [refreshKey]);

  // Format date from timestamp
  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Get photo URL by userID
  const getPhotoUrl = (userId: number) => {
    const photo = formData?.fileJson?.profilePicture?.find((p: any) =>
      p.originalname.includes(`${userId}_photo`),
    );
    return photo?.file ? photo.file : ImageNotAvailable;
  };

  // Get signature URL by userID
  const getSignatureUrl = (userId: number) => {
    const signature = formData?.fileJson?.signature?.find((s: any) =>
      s.originalname.includes(`${userId}_sig`),
    );
    return signature?.file ? signature.file : ImageNotAvailable;
  };

  // Handle image loading errors
  const handleImageError = (e: any) => {
    e.target.src = ImageNotAvailable;
  };

  // Prepare assessments data
  const assessments = formData?.formJson?.startWorkAssessment?.checklist?.map(
    (item: any) => ({
      question: item.label,
      status: item.value ? "Yes" : "No",
    }),
  );

  // Prepare risk elements data
  const riskElements =
    formData?.formJson?.startWorkAssessment?.riskelements?.map((item: any) => ({
      element: item.label,
      status: item.value ? "Yes" : "No",
      action: `${
        item.action
          ? item.action.map((action: any) => action.name).join(", ")
          : "-"
      }`,
    }));

  // Prepare confirmation message
  const confirmationsmessage = [
    {
      element: formData?.formJson?.confirmSiteSafety?.label,
      status: formData?.formJson?.confirmSiteSafety?.value ? "Yes" : "No",
    },
  ];

  return (
    <>
      <Toast ref={toast} />
      {formDataLoading ? (
        <div className="tw-h-full tw-w-full tw-bg-transparent tw-flex tw-justify-center tw-items-center tw-absolute tw-top-0 tw-left-[50px] lg:tw-left-[120px] tw-z-50">
          <ProgressSpinner />
        </div>
      ) : (
        <div className="tw-border-solid tw-border tw-border-[#E2E2e2] tw-rounded-lg tw-relative tw-bg-white">
          <div className="tw-bg-[#F8FAFC] tw-p-4 tw-rounded-t-lg tw-border-b-[1px] tw-border-[#E2E8F0] tw-flex tw-justify-between tw-items-center">
            <h2 className="tw-text-2xl tw-font-semibold tw-text-[#101828]">
              Basic Details
            </h2>
          </div>
          <div>
            <div className="tw-flex tw-flex-col tw-gap-3 tw-px-4 tw-py-4">
              <div className="tw-flex tw-items-center tw-gap-1">
                <p className="leak-detail-label">Date :</p>
                <p className="leak-value">
                  {formatDate(formData?.formJson?.date)}
                </p>
              </div>
              <div className="tw-flex tw-items-center tw-gap-1">
                <p className="leak-detail-label">Team Name :</p>
                <p className="leak-value">{formData?.formJson?.teamName}</p>
              </div>
              <div className="tw-flex tw-items-center tw-gap-1">
                <p className="leak-detail-label">DMA/Location :</p>
                <p className="leak-value">
                  {formData?.formJson?.dma_location.name}
                </p>
              </div>
              <div className="tw-flex tw-items-center tw-gap-1">
                <p className="leak-detail-label">Vehicle Registration :</p>
                <p className="leak-value">
                  {formData?.formJson.vehicleRegistration?.registration_no}
                </p>
              </div>
              <div className="tw-flex tw-items-center tw-gap-1">
                <p className="leak-detail-label">Key Plant Details :</p>
                <p className="leak-value">
                  {/* {formData?.formJson?.keyPlantDetails?.name} */}
                  {formData?.formJson?.keyPlantDetails?.map(
                    (keyPlant: any, index: number) => (
                      <span key={index}>
                        {keyPlant.name}
                        {index + 1 < formData?.formJson?.keyPlantDetails.length
                          ? ", "
                          : ""}
                      </span>
                    ),
                  )}
                </p>
              </div>
              <div className="tw-flex tw-items-center tw-gap-1">
                <p className="leak-detail-label">Activity :</p>
                <p className="leak-value">
                  {formData?.formJson?.activity?.name}
                </p>
              </div>
            </div>

            <div className="tw-flex lg:tw-gap-10 tw-gap-4 tw-items-center tw-justify-start tw-p-4 tw-flex-wrap">
              {/* Team Member 1 */}
              <div className="tw-flex tw-items-start tw-justify-start tw-gap-2 tw-flex-col">
                <div className="tw-flex tw-items-center tw-gap-1 tw-flex-wrap">
                  <p className="leak-detail-label">Team Member 1 :</p>
                  <p className="leak-value">
                    {formData?.formJson?.teamMember1?.displayName}
                  </p>
                </div>
                <p className="leak-detail-label">
                  Photo of Team Member 1 in PPE
                </p>
                <div className="tw-flex tw-gap-2 tw-items-start tw-justify-start tw-flex-wrap">
                  <Image
                    src={getPhotoUrl(formData?.formJson?.teamMember1?.userID)}
                    alt="team member"
                    className="tw-w-32 tw-shrink-0 tw-object-contain"
                    indicatorIcon={icon}
                    preview
                    loading="lazy"
                    pt={{
                      rotateRightButton: { className: "tw-text-white" },
                      downloadButton: { className: "tw-text-white" },
                      rotateLeftButton: { className: "tw-text-white" },
                      zoomInButton: { className: "tw-text-white" },
                      zoomOutButton: { className: "tw-text-white" },
                      closeButton: { className: "tw-text-white" },
                    }}
                    onError={handleImageError}
                  />
                </div>
                {/* Timestamp and Address */}
                {formData?.timeStampLocationJson && (
                  <>
                    {formData?.timeStampLocationJson?.locations?.map(
                      (location: any) => {
                        if (
                          location.userID ===
                          formData?.formJson?.teamMember1?.userID
                        ) {
                          const timestamp =
                            formData?.timeStampLocationJson?.timeStamps?.find(
                              (time: any) => time.userID === location.userID,
                            );
                          return (
                            <div key={location.userID} className="tw-mt-2">
                              <p className="tw-text-sm tw-text-gray-600">
                                <span className="tw-font-semibold">
                                  Location:
                                </span>{" "}
                                {location.address}
                              </p>
                              <p className="tw-text-sm tw-text-gray-600">
                                <span className="tw-font-semibold">
                                  Timestamp:
                                </span>{" "}
                                {timestamp?.timestamp}
                              </p>
                            </div>
                          );
                        }
                        return null;
                      },
                    )}
                  </>
                )}
              </div>

              {/* Team Member 2 */}
              {formData?.formJson?.teamMember2?.userID && (
                <div className="tw-flex tw-items-start tw-justify-start tw-gap-2 tw-flex-col">
                  <div className="tw-flex tw-items-center tw-gap-1 tw-flex-wrap">
                    <p className="leak-detail-label">Team Member 2 :</p>
                    <p className="leak-value">
                      {formData?.formJson?.teamMember2?.displayName}
                    </p>
                  </div>
                  <p className="leak-detail-label">
                    Photo of Team Member 2 in PPE
                  </p>
                  <div className="tw-flex tw-gap-2 tw-items-start tw-justify-start tw-flex-wrap">
                    <Image
                      src={getPhotoUrl(formData?.formJson?.teamMember2?.userID)}
                      alt="team member"
                      className="tw-w-32 tw-shrink-0 tw-object-contain"
                      indicatorIcon={icon}
                      preview
                      loading="lazy"
                      pt={{
                        rotateRightButton: { className: "tw-text-white" },
                        downloadButton: { className: "tw-text-white" },
                        rotateLeftButton: { className: "tw-text-white" },
                        zoomInButton: { className: "tw-text-white" },
                        zoomOutButton: { className: "tw-text-white" },
                        closeButton: { className: "tw-text-white" },
                      }}
                      onError={handleImageError}
                    />
                  </div>
                  {/* Timestamp and Address */}
                  {formData?.timeStampLocationJson && (
                    <>
                      {formData?.timeStampLocationJson?.locations?.map(
                        (location: any) => {
                          if (
                            location.userID ===
                            formData?.formJson?.teamMember2?.userID
                          ) {
                            const timestamp =
                              formData?.timeStampLocationJson?.timeStamps?.find(
                                (time: any) => time.userID === location.userID,
                              );
                            return (
                              <div key={location.userID} className="tw-mt-2">
                                <p className="tw-text-sm tw-text-gray-600">
                                  <span className="tw-font-semibold">
                                    Location:
                                  </span>{" "}
                                  {location.address}
                                </p>
                                <p className="tw-text-sm tw-text-gray-600">
                                  <span className="tw-font-semibold">
                                    Timestamp:
                                  </span>{" "}
                                  {timestamp?.timestamp}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        },
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Additional Team Members */}
              {formData?.formJson?.additionalTeamMembers?.map(
                (member: any, index: number) => {
                  const locationData =
                    formData?.timeStampLocationJson?.locations?.find(
                      (loc: any) => loc.userID === member.userID,
                    );
                  const timestampData =
                    formData?.timeStampLocationJson?.timeStamps?.find(
                      (time: any) => time.userID === member.userID,
                    );

                  return (
                    <div
                      key={member.userID}
                      className="tw-flex tw-items-start tw-justify-start tw-gap-2 tw-flex-col"
                    >
                      <div className="tw-flex tw-items-center tw-gap-1 tw-flex-wrap">
                        <p className="leak-detail-label">
                          Additional Team Member {index + 1}:
                        </p>
                        <p className="leak-value">{member.displayName}</p>
                      </div>
                      <p className="leak-detail-label">
                        Photo of additional team member {index + 1} in PPE
                      </p>
                      <div className="tw-flex tw-gap-2 tw-items-start tw-justify-start tw-flex-wrap">
                        <Image
                          src={getPhotoUrl(member.userID)}
                          alt="team member"
                          className="tw-w-32 tw-shrink-0 tw-object-contain"
                          indicatorIcon={icon}
                          preview
                          loading="lazy"
                          pt={{
                            rotateRightButton: { className: "tw-text-white" },
                            downloadButton: { className: "tw-text-white" },
                            rotateLeftButton: { className: "tw-text-white" },
                            zoomInButton: { className: "tw-text-white" },
                            zoomOutButton: { className: "tw-text-white" },
                            closeButton: { className: "tw-text-white" },
                          }}
                          onError={handleImageError}
                        />
                      </div>
                      {/* Timestamp and Address */}
                      {locationData && timestampData && (
                        <div className="tw-flex tw-flex-col tw-gap-1 tw-mt-2">
                          <p className="tw-text-sm tw-text-gray-600">
                            <span className="tw-font-semibold">Location:</span>{" "}
                            {locationData.address}
                          </p>
                          <p className="tw-text-sm tw-text-gray-600">
                            <span className="tw-font-semibold">Timestamp:</span>{" "}
                            {timestampData.timestamp}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                },
              )}
            </div>

            {/* Emergency Details */}
            <div className="tw-p-4 tw-border-b-[1px] tw-border-[#E2E8F0]">
              <h2 className="tw-text-lg tw-font-semibold tw-text-[#101828]">
                Emergency Details
              </h2>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-3 tw-px-4 tw-py-4">
              <div className="tw-flex tw-items-center tw-gap-1">
                <p className="leak-detail-label">
                  Is a first aid kit available on site:
                </p>
                <p className="leak-value">
                  {formData?.formJson.emergencyDetails.isFirstAidKitAvailable
                    ? "Yes"
                    : "No"}
                </p>
              </div>
              <div className="tw-flex tw-items-center tw-gap-1">
                <p className="leak-detail-label">
                  Is a first aider available on site:
                </p>
                <p className="leak-value">
                  {formData?.formJson.emergencyDetails.isFirstAiderAvailable
                    ? "Yes"
                    : "No"}
                </p>
              </div>
              <div className="tw-flex tw-items-center tw-gap-1">
                <p className="leak-detail-label">Closest A&E:</p>
                <p className="leak-value">
                  {formData?.formJson.emergencyDetails.aAndE.name}
                </p>
              </div>
              <div className="tw-flex tw-items-center tw-gap-1">
                <p className="leak-detail-label">
                  Details of Emergency Contact:
                </p>
                <p className="leak-value">
                  {formData?.formJson.emergencyDetails.emergencyContact.name} -
                  {formData?.formJson.emergencyDetails.emergencyContact.contact}
                </p>
              </div>
            </div>

            {/* Risk Assessment and Controls */}
            <div className="tw-p-4 tw-border-b-[1px] tw-border-[#E2E8F0]">
              <h2 className="tw-text-lg tw-font-semibold tw-text-[#101828]">
                Risk Assessment and Controls
              </h2>
            </div>
            <div className="tw-p-4">
              <DataTable value={assessments} className="tw-w-full">
                <Column
                  field="question"
                  header="Start Work Assessment"
                  className="lg:max-w-[400px] max-w-[300px]"
                />
                <Column
                  field="status"
                  header=""
                  className="tw-text-right tw-font-semibold lg:tw-w-[100px"
                  body={(rowData) => (
                    <span className={`tw-text-[#6C6C6C] tw-font-normal`}>
                      {rowData.status}
                    </span>
                  )}
                />
              </DataTable>
            </div>
            <div className="tw-p-4">
              <DataTable value={riskElements} className="tw-w-full">
                <Column
                  field="element"
                  header="Risk Elements"
                  className="lg:max-w-[400px] max-w-[300px] tw-p-3"
                />
                <Column
                  field="status"
                  header=""
                  className="tw-text-right tw-font-normal lg:tw-w-[100px]"
                  style={{ width: "100px" }}
                />
                <Column
                  field="action"
                  header=""
                  className="tw-text-right"
                  body={(rowData) => {
                    return (
                      <span className="tw-text-[#6C6C6C] tw-font-normal tw-justify-start tw-flex tw-text-start lg:tw-w-[350px] tw-w-full">
                        {/* {rowData.action} */}
                        {rowData.action}
                      </span>
                    );
                  }}
                />
              </DataTable>
            </div>

            {/* Additional Comments */}
            <div className="tw-p-4">
              <div>
                <label className="tw-font-medium tw-text-[#344054]">
                  Any Additional Comments:
                </label>
                <p className="tw-text-[#6C6C6C] tw-font-normal">
                  {formData?.formJson.additionalComments ||
                    "No additional comments"}
                </p>
              </div>
            </div>

            {/* <div className="tw-text-[#5B5B5B] tw-text-sm tw-p-4">
              <span className="tw-text-[#F00]">
                The assessment must be discussed and agreed with all other team
                members who must sign below to confirm they understand.
              </span>
            </div> */}

            <div className="tw-p-4">
              <DataTable value={confirmationsmessage} className="tw-w-full">
                <Column
                  field="element"
                  header={<span className="tw-py-3 !tw-h-10"> </span>}
                  className="lg:max-w-[400px] max-w-[300px]"
                />
                <Column
                  field="status"
                  header=""
                  className="tw-text-right tw-font-normal"
                />
              </DataTable>
            </div>

            {/* Signatures Section */}
            <div className="xl:tw-grid tw-grid-cols-3 tw-flex tw-justify-start tw-items-start tw-flex-wrap xl:tw-gap-5 tw-gap-3   tw-mt-4 tw-w-full lg:tw-w-fit tw-p-4">
              {/* Team Member 1 Signature */}
              <div className="tw-flex tw-flex-col tw-items-start tw-w-full sm:tw-w-[270px]  lg:tw-w-fit xl:tw-max-w-[380px]">
                <span className="tw-mb-2">
                  Name: {formData?.formJson.teamMember1.displayName}
                </span>
                <div className="tw-flex tw-flex-col tw-items-end tw-w-full tw-justify-end">
                  <div className="tw-border tw-rounded xl:tw-w-[370px]  tw-w-full tw-bg-gray-100 ">
                    {getSignatureUrl(formData?.formJson.teamMember1.userID) ? (
                      <img
                        src={getSignatureUrl(
                          formData?.formJson.teamMember1.userID,
                        )}
                        alt="signature"
                        className="tw-h-32 tw-object-contain"
                      />
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              </div>

              {/* Team Member 2 Signature */}
              {formData?.formJson?.teamMember2?.userID && (
                <div className="tw-flex tw-flex-col tw-items-start tw-w-full sm:tw-w-[270px]  lg:tw-w-fit xl:tw-max-w-[380px]">
                  <span className="tw-mb-2">
                    Name: {formData?.formJson.teamMember2.displayName}
                  </span>
                  <div className="tw-flex tw-flex-col tw-items-end tw-w-full tw-justify-end">
                    <div className="tw-border tw-rounded xl:tw-w-[370px]  tw-w-full tw-bg-gray-100 ">
                      {getSignatureUrl(
                        formData?.formJson.teamMember2.userID,
                      ) ? (
                        <img
                          src={getSignatureUrl(
                            formData?.formJson.teamMember2.userID,
                          )}
                          alt="signature"
                          className=" tw-h-32  tw-object-contain"
                        />
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Team Members Signatures */}
              {formData?.formJson.additionalTeamMembers?.map(
                (member: any, index: number) => (
                  <div
                    key={member.userID}
                    className="tw-flex tw-flex-col tw-items-start tw-w-full sm:tw-w-[270px]  lg:tw-w-fit xl:tw-max-w-[380px]"
                  >
                    <span className="tw-mb-2">Name: {member.displayName}</span>
                    <div className="tw-flex tw-flex-col tw-items-end tw-w-full tw-justify-end">
                      <div className="tw-border tw-rounded xl:tw-w-[370px]  tw-w-full tw-bg-gray-100 ">
                        {getSignatureUrl(member.userID) ? (
                          <img
                            src={getSignatureUrl(member.userID)}
                            alt="signature"
                            className=" tw-h-32  tw-object-contain"
                          />
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>
                  </div>
                ),
              )}
            </div>

            {/* Reviewer Section - Add this after the Signatures Section */}
            {formData?.reviewerName && (
              <div className="tw-p-4 tw-border-t-[1px] tw-border-[#E2E8F0] tw-flex tw-flex-row tw-gap-3">
                <div className="tw-flex-1">
                  <h2 className="tw-text-lg tw-font-semibold tw-text-[#101828] tw-mb-4">
                    Review Details
                  </h2>

                  <div className="tw-flex tw-flex-col tw-gap-4">
                    <div className="tw-flex tw-items-center tw-gap-1">
                      <p className="leak-detail-label">Review Date:</p>
                      <p className="leak-value">
                        {convertUTCEpochToDate(formData?.reviewerDate)}
                      </p>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-1">
                      <p className="leak-detail-label">Reviewed By:</p>
                      <p className="leak-value">{formData?.reviewerName}</p>
                    </div>
                    <div className="tw-flex tw-items-start tw-gap-1">
                      <p className="leak-detail-label">Reviewer Comments:</p>
                      <p className="leak-value">
                        {formData?.reviewerComment || "No comments provided"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="tw-flex-1 tw-flex tw-items-center tw-justify-center">
                  {formData?.reviewerSignatureVM && (
                    <div className="tw-border tw-rounded xl:tw-w-[370px] tw-w-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center">
                      <img
                        src={formData?.reviewerSignatureVM || ImageNotAvailable}
                        alt="reviewer signature"
                        className="tw-h-32 tw-object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Action Buttons */}
            <div className="tw-flex tw-justify-end tw-items-center tw-p-4 tw-gap-2 !tw-border-t-[#E2E8F0] tw-border-t-[1px]">
              <Button
                className={`tw-flex tw-px-3 tw-py-2 tw-rounded ${
                  formData?.isReviewed
                    ? "tw-bg-primary tw-border-primary tw-text-white"
                    : "!tw-border-[#C3E3F4] tw-text-[#344054]"
                } !tw-border-[#C3E3F4] tw-text-[#344054] tw-font-medium`}
                label={`${formData?.isReviewed ? "Back" : "Cancel"}`}
                onClick={() => navigate("/layout/leak-detection/team-listing")}
                color="white"
                icon={formData?.isReviewed ? "pi pi-chevron-left" : undefined}
              />
              {!formData?.isReviewed && (
                <Button
                  className="tw-flex tw-px-3 tw-py-2 tw-rounded tw-bg-primary tw-border-primary tw-text-white tw-font-medium"
                  label="Add Review"
                  onClick={() => setVisible(true)}
                />
              )}
            </div>

            <AddReviewModal
              visible={visible}
              setVisible={setVisible}
              onHide={() => setVisible(false)}
              onReviewSubmitted={() => setRefreshKey((prev) => prev + 1)} // Add this prop
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ViewDetection;
